import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle, Loader2, Search, Eye, X } from 'lucide-react';
import api from '../api';

const STYLES = `
  .co-wrap { font-family: 'Inter', sans-serif; color: #000; }
  .co-head { display:flex; justify-content:space-between; align-items:center; gap:12px; flex-wrap:wrap; margin-bottom:16px; }
  .co-title { margin:0; font-size:22px; font-weight:800; color:#111827; }
  .co-sub { color:#1a1a1a; font-size:13px; margin-top:4px; }
  .co-filters { display:grid; grid-template-columns: 1.6fr 1fr 1fr 1fr; gap:10px; margin-bottom:16px; }
  .co-input, .co-select { width:100%; box-sizing:border-box; border:1px solid #d1d5db; border-radius:10px; padding:10px 12px; font-size:13px; color:#111827; background:#fff; }
  .co-card-grid { display:grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap:14px; }
  .co-card { background:#fff; border:1px solid #e5e7eb; border-radius:14px; box-shadow: 0 2px 10px rgba(0,0,0,.05); padding:14px; }
  .co-row { display:flex; justify-content:space-between; gap:8px; margin-bottom:6px; font-size:13px; color:#000; }
  .co-design { font-size:16px; font-weight:800; color:#111827; margin-bottom:6px; }
  .co-client { font-size:13px; color:#1a1a1a; margin-bottom:10px; }
  .co-badge { display:inline-flex; align-items:center; padding:4px 10px; border-radius:999px; font-size:11px; font-weight:700; }
  .co-actions { margin-top:12px; display:flex; gap:8px; flex-wrap:wrap; }
  .co-btn { border:1px solid #d1d5db; border-radius:9px; background:#fff; color:#111827; padding:8px 10px; font-size:12px; font-weight:700; cursor:pointer; min-height:38px; }
  .co-btn:disabled { opacity:.55; cursor:not-allowed; }
  .co-btn-primary { background:#111827; color:#fff; border-color:#111827; }
  .co-btn-success { background:#16a34a; color:#fff; border-color:#16a34a; }
  .co-empty { padding:48px 12px; text-align:center; color:#1a1a1a; border:1px dashed #d1d5db; border-radius:12px; background:#fff; }
  .co-loading { padding:48px 12px; text-align:center; color:#1a1a1a; }

  .co-modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:1200; display:flex; align-items:center; justify-content:center; padding:16px; }
  .co-modal { width:100%; max-width:540px; background:#fff; border-radius:14px; box-shadow:0 12px 28px rgba(0,0,0,.25); overflow:hidden; }
  .co-modal-head { padding:14px 16px; border-bottom:1px solid #e5e7eb; display:flex; align-items:center; justify-content:space-between; }
  .co-modal-body { padding:16px; }
  .co-modal-grid { display:grid; grid-template-columns:1fr 1fr; gap:10px; font-size:13px; color:#000; }
  .co-modal-foot { padding:12px 16px; border-top:1px solid #e5e7eb; display:flex; justify-content:flex-end; gap:8px; }

  @media (max-width: 900px) { .co-filters { grid-template-columns:1fr 1fr; } }
  @media (max-width: 640px) { .co-filters { grid-template-columns:1fr; } .co-modal-grid { grid-template-columns:1fr; } }
`;

const gradeBadge = (grade) => {
  if (grade === 'A') return { bg: '#dcfce7', color: '#166534' };
  if (grade === 'B') return { bg: '#fef3c7', color: '#92400e' };
  if (grade === 'C') return { bg: '#fee2e2', color: '#991b1b' };
  return { bg: '#e5e7eb', color: '#374151' };
};

const statusBadge = (status) => {
  if (status === 'DELIVERED') return { bg: '#dcfce7', color: '#166534' };
  if (status === 'DISPATCHED') return { bg: '#dbeafe', color: '#1d4ed8' };
  return { bg: '#f3f4f6', color: '#374151' };
};

const fmtDate = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const triggerBlobDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const normalizeApiPath = (url) => {
  if (!url) return url;
  const raw = String(url).trim();
  if (!raw) return raw;
  if (/^https?:\/\//i.test(raw)) return raw;
  return raw.replace(/^\/api\/v1/, '') || '/';
};

function DetailModal({ row, onClose, onDelivered, delivering, downloadingCertificate, onDownloadCertificate }) {
  if (!row) return null;
  return (
    <div className="co-modal-overlay" onClick={onClose}>
      <div className="co-modal" onClick={(e) => e.stopPropagation()}>
        <div className="co-modal-head">
          <strong style={{ fontSize: 16, color: '#111827' }}>Completed Order Detail</strong>
          <button className="co-btn" onClick={onClose}><X size={14} /></button>
        </div>
        <div className="co-modal-body">
          <div className="co-modal-grid">
            <div><strong>Order:</strong> {row.order_number || '-'}</div>
            <div><strong>Job Card:</strong> JC-{row.job_card_id}</div>
            <div><strong>Design:</strong> {row.design_name || '-'}</div>
            <div><strong>Client:</strong> {row.client_name || '-'}</div>
            <div><strong>Pieces:</strong> {row.quantity ?? '-'}</div>
            <div><strong>Final Weight:</strong> {row.final_weight ?? '-'} g</div>
            <div><strong>QC Grade:</strong> {row.qc_grade || '-'}</div>
            <div><strong>Completed:</strong> {fmtDate(row.completed_at)}</div>
            <div><strong>Delivery:</strong> {row.delivery_status || 'PENDING'}</div>
            <div><strong>Delivered At:</strong> {fmtDate(row.delivered_at)}</div>
          </div>
        </div>
        <div className="co-modal-foot">
          {row.qc_certificate_available && row.qc_certificate_url ? (
            <button className="co-btn" onClick={() => onDownloadCertificate(row)} disabled={downloadingCertificate}>
              {downloadingCertificate ? 'Downloading...' : 'Download QC Certificate'}
            </button>
          ) : null}
          {row.delivery_status !== 'DELIVERED' ? (
            <button className="co-btn co-btn-success" disabled={delivering} onClick={() => onDelivered(row)}>
              {delivering ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />} Mark Delivered
            </button>
          ) : null}
          <button className="co-btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

export default function CompletedOrders() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [deliveryStatus, setDeliveryStatus] = useState('ALL');
  const [selected, setSelected] = useState(null);
  const [delivering, setDelivering] = useState(false);
  const [downloadingCertificate, setDownloadingCertificate] = useState(false);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/sales/completed-orders');
      setRows(res?.data?.data || []);
    } catch (e) {
      setRows([]);
      setError(e?.response?.data?.message || 'Failed to load completed orders.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return rows.filter((row) => {
      const q = search.trim().toLowerCase();
      if (q) {
        const text = [row.design_name, row.client_name, row.order_number, `jc-${row.job_card_id}`]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!text.includes(q)) return false;
      }

      if (deliveryStatus !== 'ALL' && (row.delivery_status || 'PENDING') !== deliveryStatus) return false;

      if (fromDate) {
        const dt = row.completed_at ? new Date(row.completed_at) : null;
        if (!dt || dt < new Date(`${fromDate}T00:00:00`)) return false;
      }
      if (toDate) {
        const dt = row.completed_at ? new Date(row.completed_at) : null;
        if (!dt || dt > new Date(`${toDate}T23:59:59`)) return false;
      }

      return true;
    });
  }, [rows, search, fromDate, toDate, deliveryStatus]);

  const markDelivered = async (row) => {
    setDelivering(true);
    try {
      await api.post(`/sales/orders/${row.job_card_id}/mark-delivered`, {
        delivery_date: new Date().toISOString(),
        delivery_notes: 'Delivered from Sales module',
      });
      await load();
      setSelected((prev) => (prev && prev.job_card_id === row.job_card_id ? { ...prev, delivery_status: 'DELIVERED', delivered_at: new Date().toISOString() } : prev));
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to mark delivered.');
    } finally {
      setDelivering(false);
    }
  };

  const downloadCertificate = async (row) => {
    if (!row?.qc_certificate_url) return;
    setDownloadingCertificate(true);
    setError('');
    try {
      const response = await api.get(normalizeApiPath(row.qc_certificate_url), { responseType: 'blob' });
      triggerBlobDownload(
        response.data,
        `QC_Certificate_JC-${row.job_card_id || 'order'}.pdf`
      );
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to download QC certificate.');
    } finally {
      setDownloadingCertificate(false);
    }
  };

  return (
    <>
      <style>{STYLES}</style>
      <div className="co-wrap">
        <div className="co-head">
          <div>
            <h2 className="co-title">Completed Orders</h2>
            <div className="co-sub">Sales view of completed production items ready for delivery.</div>
          </div>
          <button className="co-btn" onClick={load}>Refresh</button>
        </div>

        <div className="co-filters">
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: 11, color: '#9ca3af' }} />
            <input className="co-input" style={{ paddingLeft: 32 }} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search client/design/order" />
          </div>
          <input className="co-input" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          <input className="co-input" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          <select className="co-select" value={deliveryStatus} onChange={(e) => setDeliveryStatus(e.target.value)}>
            <option value="ALL">All Delivery Status</option>
            <option value="PENDING">Pending</option>
            <option value="DISPATCHED">Dispatched</option>
            <option value="DELIVERED">Delivered</option>
          </select>
        </div>

        {loading ? (
          <div className="co-loading"><Loader2 size={18} className="animate-spin" /> Loading completed orders...</div>
        ) : error ? (
          <div className="co-empty" style={{ color: '#b91c1c' }}>{error}</div>
        ) : filtered.length === 0 ? (
          <div className="co-empty">No completed orders found.</div>
        ) : (
          <div className="co-card-grid">
            {filtered.map((row) => {
              const grade = gradeBadge(row.qc_grade);
              const delivery = statusBadge(row.delivery_status || 'PENDING');
              return (
                <div className="co-card" key={row.job_card_id}>
                  <div className="co-design">{row.design_name || '-'}</div>
                  <div className="co-client">Client: {row.client_name || '-'}</div>
                  <div className="co-row"><span>Qty</span><strong>{row.quantity ?? '-'}</strong></div>
                  <div className="co-row"><span>Weight</span><strong>{row.final_weight ?? '-'} g</strong></div>
                  <div className="co-row"><span>Completed</span><strong>{fmtDate(row.completed_at)}</strong></div>
                  <div className="co-row"><span>QC Grade</span><span className="co-badge" style={{ background: grade.bg, color: grade.color }}>{row.qc_grade || '-'}</span></div>
                  <div className="co-row"><span>Production Status</span><strong style={{ textAlign: 'right' }}>{row.production_status || 'Completed'}</strong></div>
                  <div className="co-row"><span>Progress</span><strong>{Number(row.completion_pct || 0)}%</strong></div>
                  {row.rework_pieces_count > 0 ? (
                    <div className="co-row"><span>Rework Queue</span><strong style={{ color: '#c2410c' }}>{row.rework_pieces_count} pcs</strong></div>
                  ) : null}
                  <div className="co-row"><span>Delivery</span><span className="co-badge" style={{ background: delivery.bg, color: delivery.color }}>{row.delivery_status || 'PENDING'}</span></div>

                  <div className="co-actions">
                    <button className="co-btn" onClick={() => setSelected(row)}><Eye size={13} /> View Details</button>
                    {row.delivery_status !== 'DELIVERED' ? (
                      <button className="co-btn co-btn-primary" onClick={() => markDelivered(row)} disabled={delivering}>
                        {delivering ? 'Updating...' : 'Mark Delivered'}
                      </button>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <DetailModal
        row={selected}
        onClose={() => setSelected(null)}
        onDelivered={markDelivered}
        delivering={delivering}
        downloadingCertificate={downloadingCertificate}
        onDownloadCertificate={downloadCertificate}
      />
    </>
  );
}
